import { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import { useTranslation } from 'react-i18next';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  onSnapshot,
  query,
  orderBy
} from 'firebase/firestore';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  X, 
  Save,
  Loader2
} from 'lucide-react';
import { logActivity } from '@/lib/logger';


async function autoTranslate(text: string, from: string, to: string): Promise<string> {
  try {
    const response = await fetch(`https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${from}|${to}`);
    const data = await response.json();
    if (data.responseData && data.responseData.translatedText) {
      return data.responseData.translatedText;
    }
    return text;
  } catch (error) {
    console.error("Translation API error:", error);
    return text;
  }
}

interface Category {
  id: string;
  name: string | {
    es: string;
    en: string;
  };
}

export default function CategoryManagementPage() {
  const { t, i18n } = useTranslation();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [nameEn, setNameEn] = useState('');
  const [nameEs, setNameEs] = useState('');


  useEffect(() => {
    const q = query(collection(db, 'categories'), orderBy('name'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const catData = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      })) as Category[];
      setCategories(catData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleOpenModal = (category: Category | null = null) => {
    if (category) {
      setEditingCategory(category);
      if (typeof category.name === 'string') {
        setNameEn(category.name);
        setNameEs('');
      } else {
        setNameEn(category.name.en || '');
        setNameEs(category.name.es || '');
      }
    } else {
      setEditingCategory(null);
      setNameEn('');
      setNameEs('');
    }
    setIsModalOpen(true);
  };



  const handleTranslate = async (from: string, to: string) => {
    const text = from === 'en' ? nameEn : nameEs;
    if (!text.trim()) return;
    
    const translated = await autoTranslate(text, from, to);
    if (to === 'es') {
      setNameEs(translated);
    } else {
      setNameEn(translated);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nameEn.trim() && !nameEs.trim()) return;

    const nameData = {
      en: nameEn.trim(),
      es: nameEs.trim()
    };

    try {
      if (editingCategory) {
        await updateDoc(doc(db, 'categories', editingCategory.id), {
          name: nameData
        });
        await logActivity('edit', 'categories', `Category: ${nameEn}`);
      } else {
        await addDoc(collection(db, 'categories'), {
          name: nameData,
          createdAt: new Date().toISOString()
        });
        await logActivity('create', 'categories', `Category: ${nameEn}`);
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error saving category:", error);
    }
  };

  const handleDelete = async (id: string) => {
    const category = categories.find(c => c.id === id);
    if (window.confirm(`Are you sure you want to delete "${category?.name}"?`)) {
      await deleteDoc(doc(db, 'categories', id));
      if (category) {
        await logActivity('delete', 'categories', `Category: ${category.name}`);
      }
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-white uppercase tracking-tight">{t('admin_categories_title')}</h1>
          <p className="text-white/50 text-sm">{t('admin_categories_sub')}</p>
        </div>
        <div className="flex gap-4">

          <button
            onClick={() => handleOpenModal()}
            className="bg-[#F57F25] hover:bg-[#e06d15] text-black font-black px-6 py-3 rounded-2xl flex items-center gap-2 transition-all uppercase text-sm"
          >
            <Plus size={20} />
            {t('admin_categories_new')}
          </button>
        </div>
      </div>

      <div className="bg-[#111] border border-white/5 rounded-[32px] overflow-hidden shadow-2xl">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="animate-spin text-[#F57F25]" size={32} />
          </div>
        ) : categories.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center space-y-4">
            <FolderTree size={48} className="text-white/10" />
            <p className="text-white/40 font-medium">{t('admin_categories_empty')}</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {categories.map((cat) => {
              const displayName = typeof cat.name === 'string' ? cat.name : (cat.name[i18n.language] || cat.name.en || '');
              return (
                <div key={cat.id} className="flex items-center justify-between p-6 hover:bg-white/5 transition-colors group">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#F57F25]/10 rounded-xl flex items-center justify-center text-[#F57F25] font-black italic">
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-white font-bold uppercase tracking-tight text-lg">{displayName}</span>
                  </div>
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      onClick={() => handleOpenModal(cat)}
                      className="p-3 text-blue-400 hover:bg-blue-400/10 rounded-xl transition-all"
                    >
                      <Pencil size={18} />
                    </button>
                    <button 
                      onClick={() => handleDelete(cat.id)}
                      className="p-3 text-red-400 hover:bg-red-400/10 rounded-xl transition-all"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] w-full max-w-md rounded-[40px] border border-white/10 shadow-2xl overflow-hidden p-10">
            <div className="flex justify-between items-center mb-10">
              <h2 className="text-2xl font-black text-white uppercase tracking-tight">
                {editingCategory ? t('admin_categories_edit') : t('admin_categories_new')}
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="text-white/40 hover:text-white transition-colors">
                <X size={28} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-white/50 text-[10px] font-black uppercase tracking-widest ml-1">Name (English)</label>
                    <button
                      type="button"
                      onClick={() => handleTranslate('es', 'en')}
                      className="text-[#F57F25] text-[10px] font-black uppercase tracking-widest hover:text-[#e06d15] transition-colors"
                    >
                      Translate from Spanish
                    </button>
                  </div>
                  <input
                    type="text"
                    autoFocus
                    value={nameEn}
                    onChange={e => setNameEn(e.target.value)}
                    className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none transition-all text-lg"
                    placeholder="e.g. Burgers"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-white/50 text-[10px] font-black uppercase tracking-widest ml-1">Nombre (Español)</label>
                    <button
                      type="button"
                      onClick={() => handleTranslate('en', 'es')}
                      className="text-[#F57F25] text-[10px] font-black uppercase tracking-widest hover:text-[#e06d15] transition-colors"
                    >
                      Traducir de Inglés
                    </button>
                  </div>
                  <input
                    type="text"
                    value={nameEs}
                    onChange={e => setNameEs(e.target.value)}
                    className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none transition-all text-lg"
                    placeholder="ej. Hamburguesas"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                className="w-full bg-[#F57F25] hover:bg-[#e06d15] text-black font-black py-5 rounded-[20px] transition-all flex items-center justify-center gap-3 uppercase text-sm shadow-xl shadow-[#F57F25]/20"
              >
                <Save size={22} /> {t('admin_categories_save')}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
